import { Component, DestroyRef, OnInit, Output, EventEmitter, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {FormGroup, FormBuilder} from '@angular/forms';
import {Observable, of} from 'rxjs';
import {finalize, tap, first, switchMap, map} from 'rxjs/operators';

import {FeatureFlags, FeatureFlagService} from '@core';

import {DropdownItem, NotificationService, SharedModule} from '@shared';

import {
    TaskService,
    OfficeInfo,
    ProblemInstanceService,
    PatientTaskService,
    ConfigurableOfficeInfo
} from 'planner-shared';

import { OptimizationSettingsTravelTimeDurationBtnGroupComponent } from './travel-time-duration-btn-group/optimization-settings-travel-time-duration-btn-group.component';
import { OptimizationSettingsTasksDurationBtnGroupComponent } from './tasks-duration-btn-group/optimization-settings-tasks-duration-btn-group.component';
import { OptimizationSettingsHeavyTasksBtnGroupComponent } from './heavy-tasks-btn-group/optimization-settings-heavy-tasks-btn-group.component';
import { OptimizationSettingsTravelTimeDistanceBtnGroupComponent } from './travel-time-distance-btn-group/optimization-settings-travel-time-distance-btn-group.component';

@Component({
    selector: 'app-optimization-settings',
    styles: ['.dropdowns { display: inline-table;  width: 100px; }'],
    templateUrl: './optimization-settings.component.html',
    imports: [
        SharedModule,
        OptimizationSettingsTravelTimeDurationBtnGroupComponent,
        OptimizationSettingsTasksDurationBtnGroupComponent,
        OptimizationSettingsHeavyTasksBtnGroupComponent,
        OptimizationSettingsTravelTimeDistanceBtnGroupComponent
    ]
})
export class OptimizationSettingsComponent implements OnInit {
    private readonly formBuilder = inject(FormBuilder);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly patientTaskService = inject(PatientTaskService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly notificationService = inject(NotificationService);
    private readonly taskService = inject(TaskService);
    private readonly destroyRef = inject(DestroyRef);

    public settingsForm: FormGroup;
    public heavyTaskCapacityError: boolean;
    public checkingHeavyTaskCapacityError: boolean;
    public shiftTypes: DropdownItem[];
    public maximumWalkingTimes: DropdownItem[];
    public maximumDrivingTimes: DropdownItem[];
    public maximumBikingTimes: DropdownItem[];
    public existHeavyTasks$: Observable<boolean>;
    public showGroupTasksByPatientAndSkillRequirementFeature$: Observable<boolean>;
    public isRideSharingEnabledOnClient: boolean;
    public prioritizePatientTasks: Observable<boolean>;
    public prioritizePatientTasksFeature: boolean;
    public travelTimeDistanceBaselineFeature: boolean;

    @Output() settingsChange = new EventEmitter<void>();
    @Output() heavyTaskCapacityLoad = new EventEmitter<boolean>();

    private officeInfo: OfficeInfo;

    private readonly parkingTimeMinutesDefault = 1;
    // Default distance threshold = the maximum selectable value (matches the backend default).
    private readonly minimumTravelTimeDistanceMetersDefault = 500;
    private readonly minimumTravelTimeMinutesDefault = 0;
    private readonly solverRunTimeMinutesDefault = 3;
    private readonly patientTaskDurationAdjustmentProportionDefault = 0;
    private readonly travelTimeAdjustmentProportionDefault = 0;
    private readonly maximumNumberOfHeavyTasksPerEmployeeDefault = null;

    constructor() {
        this.maximumDrivingTimes = [
            <DropdownItem>{value: null, label: 'No limits'},
        ];
        this.maximumWalkingTimes = [];
        this.maximumBikingTimes = [];
        for (let minutes = 1; minutes <= 120; minutes += 1) {
            if (minutes >= 15 && minutes % 5 === 0) {
                this.maximumDrivingTimes.push(<DropdownItem>{value: minutes * 60, label: minutes.toString()});
            }
            if (minutes >= 1 && minutes <= 10) {
                this.maximumWalkingTimes.push(<DropdownItem>{value: minutes * 60, label: minutes.toString()});
            }
            if (minutes >= 1 && minutes <= 30) {
                this.maximumBikingTimes.push(<DropdownItem>{value: minutes * 60, label: minutes.toString()});
            }
        }
    }

    ngOnInit(): void {
        this.settingsForm = this.formBuilder.group({
            parkingTimeMinutes: [this.parkingTimeMinutesDefault],
            solverRunTimeMinutes: [this.solverRunTimeMinutesDefault],
            patientTaskDurationAdjustmentProportion: [this.patientTaskDurationAdjustmentProportionDefault],
            travelTimeAdjustmentProportion: [this.travelTimeAdjustmentProportionDefault],
            maximumNumberOfHeavyTasksPerEmployee: [this.maximumNumberOfHeavyTasksPerEmployeeDefault],
            shiftTypes: [null],
            useWorkBalance: [false],
            useGroupTasksByPatientAndSkillRequirement: [false],
            preferAssigningTasksToSameRosterDepartment: [false],
            maximumDrivingTimes: [null],
            maximumWalkingTimes: [null],
            maximumBikingTimes: [null],
            prioritizePatientTasks: [false],
            minimumTravelTimeDistanceMeters: [this.minimumTravelTimeDistanceMetersDefault],
            minimumTravelTimeMinutes: [this.minimumTravelTimeMinutesDefault]
        });

        this.settingsForm.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.settingsChange.emit());

        this.problemInstanceService.getProblemInstance().subscribe(result => {
            this.officeInfo = result.problemInstance.officeInfo;
            this.shiftTypes = result.problemInstance.shiftTypes?.map(
                type => <DropdownItem>{label: type, value: type}
            );
            this.setSettings(this.officeInfo);
            this.isRideSharingEnabledOnClient = result.problemInstance.officeInfo.rideSharingEnabledOnClientSide;
        });

        this.featureFlagService.isEnabled(FeatureFlags.PrioritizePatientTasks)
            .subscribe(featureValue => this.prioritizePatientTasksFeature = featureValue);
        this.featureFlagService.isEnabled(FeatureFlags.TravelTimeDistanceBaseline)
            .subscribe(featureValue => this.travelTimeDistanceBaselineFeature = featureValue);
        this.showGroupTasksByPatientAndSkillRequirementFeature$ =
            this.featureFlagService.isEnabled(FeatureFlags.GroupTasksByPatientAndSkillRequirementFeature);

        this.existHeavyTasks$ = this.taskService
            .existHeavyTasks()
            .pipe(switchMap(exist => (exist ? this.checkHeavyTaskCapacityError().pipe(map(() => true)) : of(false))));
    }

    public workBalanceChange(): void {
        if (this.settingsForm.value.useWorkBalance) {
            this.notificationService.warning('BALANCED_WORKLOAD_WARNING');
        }
    }

    public prioritizePatientTasksChange(): void {
        if (this.settingsForm.value.prioritizePatientTasks) {
            this.notificationService.warning('PRIORITIZE_PATIENT_TASKS_WARNING');
        }
    }

    public groupTasksByPatientAndSkillRequirementChange(): void {
        if (this.settingsForm.value.useGroupTasksByPatientAndSkillRequirement) {
            this.notificationService.warning('GROUP_TASKS_BY_PATIENT_AND_SKILL_WARNING');
        }
    }

    public reset(officeInfo?: OfficeInfo): void {
        if (officeInfo) {
            this.setSettings(officeInfo);
        } else {
            this.settingsForm.controls.useWorkBalance.setValue(false, { emitEvent: false });
            this.settingsForm.controls.useGroupTasksByPatientAndSkillRequirement.setValue(false, { emitEvent: false });
            this.settingsForm.controls.preferAssigningTasksToSameRosterDepartment.setValue(false, { emitEvent: false });
            this.settingsForm.controls.prioritizePatientTasks.setValue(false, { emitEvent: false });
            this.settingsForm.controls.shiftTypes.setValue(null, { emitEvent: false });
            this.settingsForm.controls.parkingTimeMinutes.setValue(this.parkingTimeMinutesDefault, { emitEvent: false });
            this.settingsForm.controls.minimumTravelTimeDistanceMeters.setValue(
                this.minimumTravelTimeDistanceMetersDefault, { emitEvent: false }
            );
            this.settingsForm.controls.minimumTravelTimeMinutes.setValue(
                this.minimumTravelTimeMinutesDefault, { emitEvent: false }
            );
            this.settingsForm.controls.solverRunTimeMinutes.setValue(this.solverRunTimeMinutesDefault, { emitEvent: false });
            this.settingsForm.controls.patientTaskDurationAdjustmentProportion.setValue(
                this.patientTaskDurationAdjustmentProportionDefault, { emitEvent: false }
            );
            this.settingsForm.controls.travelTimeAdjustmentProportion.setValue(
                this.travelTimeAdjustmentProportionDefault, { emitEvent: false }
            );
            this.settingsForm.controls.maximumNumberOfHeavyTasksPerEmployee.setValue(
                this.maximumNumberOfHeavyTasksPerEmployeeDefault, { emitEvent: false }
            );
            this.settingsForm.controls.shiftTypes.setValue(null, { emitEvent: false });
            this.settingsForm.controls.maximumDrivingTimes.setValue(null, { emitEvent: false });
            this.settingsForm.controls.maximumBikingTimes.setValue(null, { emitEvent: false });
            this.settingsForm.controls.maximumWalkingTimes.setValue(null, { emitEvent: false });
        }
    }

    public updateProblemInstance(): void {
        this.mapConfiguration(this.officeInfo);
    }

    public mapConfiguration<T extends ConfigurableOfficeInfo>(configuration: T): void {
        configuration.parkingTimeMinutes = this.settingsForm.value.parkingTimeMinutes;
        configuration.minimumTravelTimeDistanceThresholdMeters = this.travelTimeDistanceBaselineFeature
            ? this.settingsForm.value.minimumTravelTimeDistanceMeters : this.minimumTravelTimeDistanceMetersDefault;
        configuration.minimumTravelTimeBaselineMinutes = this.travelTimeDistanceBaselineFeature
            ? this.settingsForm.value.minimumTravelTimeMinutes : this.minimumTravelTimeMinutesDefault;
        configuration.solverRunTimeMinutes = this.settingsForm.value.solverRunTimeMinutes;
        configuration.patientTaskDurationAdjustmentProportionShifts = this.settingsForm.value.shiftTypes;
        configuration.useWorkBalance = this.settingsForm.value.useWorkBalance;
        configuration.useGroupTasksByPatientAndSkillRequirement
            = this.settingsForm.value.useGroupTasksByPatientAndSkillRequirement;
        configuration.preferAssigningTasksToSameRosterDepartment
            = this.settingsForm.value.preferAssigningTasksToSameRosterDepartment;
        configuration.patientTaskDurationAdjustmentProportion =
            this.settingsForm.value.patientTaskDurationAdjustmentProportion / 100;
        configuration.travelTimeAdjustmentProportion =
            this.settingsForm.value.travelTimeAdjustmentProportion / 100;
        configuration.maximumNumberOfHeavyTasksPerEmployee =
            this.settingsForm.value.maximumNumberOfHeavyTasksPerEmployee;
        configuration.maximumDrivingTime = this.settingsForm.value.maximumDrivingTimes;
        configuration.maximumWalkingTime = this.settingsForm.value.maximumWalkingTimes;
        configuration.maximumBikingTime = this.settingsForm.value.maximumBikingTimes;
        configuration.prioritizePatientTasks = this.prioritizePatientTasksFeature ? this.settingsForm.value.prioritizePatientTasks : false;
    }

    private setSettings(officeInfo: OfficeInfo): void {
        this.settingsForm.controls.useWorkBalance.setValue(officeInfo.useWorkBalance, { emitEvent: false });
        this.settingsForm.controls.useGroupTasksByPatientAndSkillRequirement
            .setValue(officeInfo.useGroupTasksByPatientAndSkillRequirement, { emitEvent: false });
        this.settingsForm.controls.preferAssigningTasksToSameRosterDepartment
            .setValue(officeInfo.preferAssigningTasksToSameRosterDepartment, { emitEvent: false });
        this.settingsForm.controls.shiftTypes.setValue(officeInfo.patientTaskDurationAdjustmentProportionShifts, { emitEvent: false });
        this.settingsForm.controls.prioritizePatientTasks.setValue(officeInfo.prioritizePatientTasks, { emitEvent: false });
        this.settingsForm.controls.parkingTimeMinutes.setValue(officeInfo.parkingTimeMinutes, { emitEvent: false });
        this.settingsForm.controls.minimumTravelTimeDistanceMeters.setValue(
            officeInfo.minimumTravelTimeDistanceThresholdMeters ?? this.minimumTravelTimeDistanceMetersDefault, { emitEvent: false }
        );
        this.settingsForm.controls.minimumTravelTimeMinutes.setValue(
            officeInfo.minimumTravelTimeBaselineMinutes ?? this.minimumTravelTimeMinutesDefault, { emitEvent: false }
        );
        this.settingsForm.controls.solverRunTimeMinutes.setValue(officeInfo.solverRunTimeMinutes, { emitEvent: false });

        this.settingsForm.controls.patientTaskDurationAdjustmentProportion.setValue(
            officeInfo.patientTaskDurationAdjustmentProportion * 100, { emitEvent: false }
        );
        this.settingsForm.controls.travelTimeAdjustmentProportion.setValue(
            officeInfo.travelTimeAdjustmentProportion * 100, { emitEvent: false }
        );
        this.settingsForm.controls.maximumNumberOfHeavyTasksPerEmployee.setValue(
            officeInfo.maximumNumberOfHeavyTasksPerEmployee > 8
                ? this.maximumNumberOfHeavyTasksPerEmployeeDefault
                : officeInfo.maximumNumberOfHeavyTasksPerEmployee, { emitEvent: false }
        );
        this.settingsForm.controls.maximumDrivingTimes.setValue(
            officeInfo.maximumDrivingTime, { emitEvent: false }
        );
        this.settingsForm.controls.maximumWalkingTimes.setValue(
            officeInfo.maximumWalkingTime, { emitEvent: false }
        );
        this.settingsForm.controls.maximumBikingTimes.setValue(
            officeInfo.maximumBikingTime, { emitEvent: false }
        );
    }

    private checkHeavyTaskCapacityError(): Observable<boolean> {
        this.checkingHeavyTaskCapacityError = true;
        this.heavyTaskCapacityLoad.emit(true);

        return this.patientTaskService.existPatientTaskWithHeavyTaskCapacityError().pipe(
            first(),
            tap(exist => {
                this.heavyTaskCapacityError = exist;
                setTimeout(() =>
                    this.settingsForm.controls.maximumNumberOfHeavyTasksPerEmployee.setErrors(
                        exist ? {capacityError: true} : null
                    )
                );
            }),
            finalize(() => {
                this.heavyTaskCapacityLoad.emit(false);
                this.checkingHeavyTaskCapacityError = false;
            })
        );
    }
}
